import {
  concretiseParams,
  updateSearchParamsWithFilters,
} from "../filters/filters"
import { updateSearchParamsWithPagination } from "../../page"
import { API_BASE_URL } from "../../app/config"
import { getToken, handleSignIn } from "../iam/middleware"

/**
 * Get named priority lists containing groupings of objects that we wish to see and their priorities.
 *
 * @param filers  // filters to apply
 * @param page  // pagination settings
 * @returns {Promise<any>}
 */
export const getPriorityLists = async ({ filter = {}, page = {} }) => {
  // Build parameter set
  const params = new URLSearchParams()
  // Add the relevant filters
  updateSearchParamsWithFilters(params, filter)
  // Replace any relative ones with absolute ones
  concretiseParams(params)
  // Pagination control
  updateSearchParamsWithPagination(params, page)

  let resp = await fetch(
    `${API_BASE_URL}/api/priorities?${params.toString()}`,
    {
      method: "GET",
      mode: "cors",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${getToken()}`,
      },
      redirect: "error",
    }
  )
  resp = handleSignIn(resp)
  return resp.json()
}

/**
 * Get a priority list by Id
 * @param priorityId
 * @returns {Promise<any>}
 */
export const getPriorityListById = async (priorityId) => {
  let resp = await fetch(`${API_BASE_URL}/api/priorities/${priorityId}`, {
    method: "GET",
    mode: "cors",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${getToken()}`,
    },
    redirect: "error",
  })

  resp = handleSignIn(resp)
  return resp.json()
}

/** Get a priority list by ID, and also enclose structured information about
 * all the RSOs in this list in a single call. Useful to avoid having to
 * fetch a large number of RSOs as a separate, reactive step.
 */
export const getPriorityListByIdExpanded = async (priorityId) => {
  let resp = await fetch(
    `${API_BASE_URL}/api/priorities/${priorityId}/with-rso-data`,
    {
      method: "GET",
      mode: "cors",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${getToken()}`,
      },
      redirect: "error",
    }
  )

  resp = handleSignIn(resp)
  return resp.json()
}

/**
 * Create a new priority list
 *
 * @param request  // the details of the list
 * @returns {Promise<any>}
 */
export const createPriorityList = async (req) => {
  let resp = await fetch(`${API_BASE_URL}/api/priorities`, {
    method: "POST",
    mode: "cors",
    headers: {
      Authorization: `Bearer ${getToken()}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(req),
    redirect: "error",
  })

  resp = handleSignIn(resp)
  return resp.json()
}

/**
 * Delete a priority list by its ID
 *
 * @param priorityId  // id of the priority list we want to delete
 * @returns {Promise<any>}
 */
export const deletePriorityListById = async (priorityId) => {
  let resp = await fetch(`${API_BASE_URL}/api/priorities/${priorityId}`, {
    method: "DELETE",
    mode: "cors",
    headers: {
      Authorization: `Bearer ${getToken()}`,
    },
    cache: "no-cache",
    redirect: "error",
  })
  resp = handleSignIn(resp)
  return resp.json()
}

/**
 * Delete a priority list by its ID
 *
 * @param priorityId  // id of the priority list we want to delete
 * @returns {Promise<any>}
 */
export const deletePriorityListsByIds = async (priorityIds) => {
  let resp = await fetch(`${API_BASE_URL}/api/priorities?ids=${priorityIds}`, {
    method: "DELETE",
    mode: "cors",
    headers: {
      Authorization: `Bearer ${getToken()}`,
    },
    cache: "no-cache",
    redirect: "error",
  })
  resp = handleSignIn(resp)
  return resp.json()
}

/**
 * DEPRECATED: Set a priority list to active or inactive.
 * @param priorityId
 * @param active a boolean value
 * @param _etag this comes from the retrieve request and is used to support optimistic concurrency
 * @returns {Promise<any>}
 */
export const setPriorityListActive = async ({ priority_id, active, _etag }) => {
  let resp = await fetch(
    `${API_BASE_URL}/api/priorities/${priority_id}/active`,
    {
      method: "PUT",
      mode: "cors",
      headers: {
        Authorization: `Bearer ${getToken()}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        priority_id: priority_id,
        active: active,
        _etag: _etag,
      }),
      redirect: "error",
    }
  )
  resp = handleSignIn(resp)
  return resp.json()
}

/**
 * Sets the astrosites that a priority list is active for. An empty
 * list means that it's not active for any.
 * @returns {Promise<any>}
 */
export const setActiveForAstrosites = async ({
  priority_id,
  astrosite_ids,
  _etag,
}) => {
  let resp = await fetch(
    `${API_BASE_URL}/api/priorities/${priority_id}/active`,
    {
      method: "PUT",
      mode: "cors",
      headers: {
        Authorization: `Bearer ${getToken()}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        priority_id: priority_id,
        astrosite_ids: astrosite_ids || [],
        _etag: _etag,
      }),
      redirect: "error",
    }
  )
  resp = handleSignIn(resp)
  return resp.json()
}

/**
 * Set a priority list's priority
 * @param priorityId
 * @param priority a number indicating the priority of the list
 * @param _etag this comes from the retrieve request and is used to support optimistic concurrency
 * @returns {Promise<any>}
 */
export const setPriorityListPriority = async ({
  priority_id,
  priority,
  _etag,
}) => {
  let resp = await fetch(
    `${API_BASE_URL}/api/priorities/${priority_id}/priority`,
    {
      method: "PUT",
      mode: "cors",
      headers: {
        Authorization: `Bearer ${getToken()}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        priority_id: priority_id,
        priority: priority,
        _etag: _etag,
      }),
      redirect: "error",
    }
  )
  resp = handleSignIn(resp)
  return resp.json()
}

/**
 * Set a priority list's name
 * @param priorityId
 * @param name
 * @param _etag this comes from the retrieve priority list request and is used to support optimistic concurrency
 * @returns {Promise<any>}
 */
export const setPriorityListName = async ({ priority_id, name, _etag }) => {
  let resp = await fetch(`${API_BASE_URL}/api/priorities/${priority_id}/name`, {
    method: "PUT",
    mode: "cors",
    headers: {
      Authorization: `Bearer ${getToken()}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      priority_id: priority_id,
      name: name,
      _etag: _etag,
    }),
    redirect: "error",
  })
  resp = handleSignIn(resp)
  return resp.json()
}

/**
 * Set a priority list's description
 * @param priorityId
 * @param description
 * @param _etag this comes from the retrieve priority list request and is used to support optimistic concurrency
 * @returns {Promise<any>}
 */
export const setPriorityListDescription = async ({
  priority_id,
  description,
  _etag,
}) => {
  let resp = await fetch(
    `${API_BASE_URL}/api/priorities/${priority_id}/description`,
    {
      method: "PUT",
      mode: "cors",
      headers: {
        Authorization: `Bearer ${getToken()}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        priority_id: priority_id,
        description: description,
        _etag: _etag,
      }),
      redirect: "error",
    }
  )
  resp = handleSignIn(resp)
  return resp.json()
}

/**
 * Set a priority list's norad ids
 * @param priorityId
 * @param norad_ids
 * @param _etag this comes from the retrieve priority list request and is used to support optimistic concurrency
 * @returns {Promise<any>}
 */
export const setPriorityListNoradIds = async ({
  priority_id,
  norad_ids = [],
  _etag,
}) => {
  let resp = await fetch(
    `${API_BASE_URL}/api/priorities/${priority_id}/norad_ids`,
    {
      method: "PUT",
      mode: "cors",
      headers: {
        Authorization: `Bearer ${getToken()}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        priority_id: priority_id,
        norad_ids: norad_ids.map((id) => "" + id), // DOH - Api needs strings
        _etag: _etag,
      }),
      redirect: "error",
    }
  )
  resp = handleSignIn(resp)
  return resp.json()
}

/**
 * Service to query for a matching set of RSO's and then add them to a new priority list
 *
 * @param filers  // filters to apply
 * @param page  // pagination settings
 * @returns {Promise<any>}
 */
export const priorityListFromRSOs = async ({
  filter = {},
  name,
  description,
  priority = 0,
  active_for_astrosites = [],
  list_id,
}) => {
  // Added list create items to params (we decide not to mix query and form params/json).
  // We keep using query params so that we can use a single filters parser across the ListMatching rsos service and add matching
  // ROS's to priority new priority list
  filter.name = name
  filter.description = description
  filter.priority = priority
  if (active_for_astrosites?.length > 0) {
    filter.active_for_astrosites = active_for_astrosites.join(",")
  }
  filter.list_id = list_id

  // Build parameter set
  const params = new URLSearchParams()
  updateSearchParamsWithFilters(params, filter)
  // Replace any relative ones with absolute ones
  concretiseParams(params)

  let resp = await fetch(
    `${API_BASE_URL}/api/priorities/matching-rsos?${params.toString()}`,
    {
      method: "POST",
      mode: "cors",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${getToken()}`,
      },
      redirect: "error",
    }
  )

  resp = handleSignIn(resp)
  return resp.json()
}
